import { useToast } from "@chakra-ui/react";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { featureViewQueryKeys } from "@/app/data/featureViewQueryKeys";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { UpsertForm } from "@/common/types/upsertForm";
import api from "@/common/utils/api";
import { useFlagsTableState } from "@/feature/hooks/useFlagsTableState";
import { ErrorResponse } from "@reflag/shared/api";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";

export const useFlagViewUpsertMutations = (
  view: FlagViewDTO,
  flagIds: string[],
  options?: Omit<
    UseMutationOptions<FlagViewDTO, AxiosError<ErrorResponse>, UpsertForm>,
    "mutationFn"
  >,
) => {
  const { app } = useEnvironment();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { sorting, onSortingChange, columnStates, onColumnStatesChange } =
    useFlagsTableState(view);

  return useMutation({
    ...options,
    mutationFn: async (values: UpsertForm) => {
      if (values.action === "update") {
        return api
          .patch<"/apps/:appId/feature-views/:viewId">(
            `/apps/${app.id}/feature-views/${view.id}`,
            {
              // Cast to any to bypass type mismatch between flag and feature column types
              // The API accepts both types
              columns: columnStates as any,
              columnSortKey: sorting[0]?.id as any,
              columnSortDesc: sorting[0]?.desc,
            },
          )
          .then((res) => res.data as unknown as FlagViewDTO);
      } else {
        // Create a new view
        return api
          .post<"/apps/:appId/feature-views">(`/apps/${app.id}/feature-views`, {
            name: values.name,
            featureIds: flagIds,
            // Cast to any to bypass type mismatch between flag and feature column types
            // The API accepts both types
            columns: columnStates as any,
            columnSortKey: sorting[0]?.id as any,
            columnSortDesc: sorting[0]?.desc,
          })
          .then((res) => res.data as unknown as FlagViewDTO);
      }
    },
    onSuccess: async (data, variables, context) => {
      toast({
        title:
          variables.action === "update" ? "View updated" : "New view created",
        status: "success",
      });

      onSortingChange([]);
      onColumnStatesChange([]);

      // Wait for views to be re-fetched
      await queryClient.invalidateQueries({
        queryKey: featureViewQueryKeys.list(app.id),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};
